import api from './api';
import { resolvePhotoUrl } from './photoUrl';
import { rolesWith } from './permissions';

/*
 * Asking the AI about photographs that are not yet on a record: who may, how
 * many, and the upload that turns local files into the storage keys
 * POST /ai/identify reads. Shared by the Identify page and the field forms'
 * "Identify with AI" (components/field/AiFieldSuggestion.js).
 */

/** The roles that may request identifications (routes/ai.py REQUEST_ROLES). */
export const AI_ROLES = rolesWith('ai:identify');
export const MAX_PHOTOS = 5;

export const mayRequestAi = user => (user?.roles || []).some(r => AI_ROLES.includes(r));

/** The roles that identify any record (routes/identifications.py ELEVATED_ROLES). */
export const IDENTIFY_ANY_ROLES = ['collection_manager', 'internal_expert', 'lab_technician'];

/** Whether this person may identify this field collection: their own, or any
 *  for IDENTIFY_ANY_ROLES -- the server's _check_can_identify, which the AI on
 *  a record's photographs (POST /ai/identify {photo_ids}) and the link of its
 *  answer follow too. */
export const mayIdentifyRecord = (user, record) => !!record && (
  (user?.roles || []).some(r => IDENTIFY_ANY_ROLES.includes(r))
  || (!!record.collector_id && String(record.collector_id) === String(user?.user_id)));

/**
 * Ask the cloud's inference function to load its models now (POST /ai/warm,
 * routes/ai.py warm_inference): a cold one takes longer to load than an
 * identification waits. Its answer also says when no identification would
 * come at all -- dev and staging (`reason: prod_only`) or no AI service
 * (`configured: false`) -- so a caller can say so before uploading anything.
 * Never rejects: null when it could not ask.
 */
export async function warmAi(config) {
  try {
    const res = await (config ? api.post('/ai/warm', undefined, config) : api.post('/ai/warm'));
    const data = res?.data || {};
    return { prodOnly: data.reason === 'prod_only', configured: data.configured !== false, warming: !!data.warming };
  } catch {
    return null;
  }
}

/**
 * A cold inference Lambda takes about 50 s to load its models, longer than the
 * API waits (20 s), so the first identification after an idle spell answers
 * 503 ai_busy while that invocation carries on and warms the function. A
 * panel tries again by itself, after the load should be done, before it asks
 * the person to: the first wait covers the rest of the load (a retry sent
 * while it is still running would start a second cold copy), the others are
 * shorter.
 */
export const AUTO_RETRY_DELAYS_MS = [30000, 15000, 15000];

/**
 * What a failed POST /ai/identify means (routes/ai.py), in the words the ai
 * namespace has for it: {status, code, key, busy, detail}. A 503 is one of
 * several states -- busy (a cold model loading, worth trying again: `busy`),
 * unreachable (no retry helps; `detail` says what is missing), prod-only (dev
 * and staging, on purpose), the desktop's pack that was not started
 * (`blocked`), else not configured. A 429 is the daily limit, or the day's
 * capacity for metered accounts (`capacity`); a 402 is a metered account
 * with no AI credits left (`creditsExhausted`, utils/aiCredits.js), and
 * `credits` then carries what the server counted. Anything else failed.
 */
export function aiFailure(e) {
  const status = e?.response?.status ?? null;
  const data = e?.response?.data || {};
  const code = data.code || null;
  const busy = status === 503 && code !== 'ai_unreachable' && code !== 'ai_prod_only'
    && (code === 'ai_busy' || !!data.retryable);
  let key = 'notConfigured';
  if (status === 402) key = 'creditsExhausted';
  else if (status === 429) key = code === 'metered_capacity_reached' ? 'capacity' : 'quota';
  else if (status !== 503) key = 'failed';
  else if (code === 'ai_unreachable') key = 'unreachable';
  else if (code === 'ai_prod_only') key = 'prodOnly';
  else if (busy) key = 'busy';
  else if (data.blocked) key = 'blocked';
  return { status, code, key, busy, detail: code === 'ai_unreachable' ? (data.detail || '') : '',
    credits: data.credits || null };
}

/** `config` goes with each presign request when given (Field Mode's camera
 *  sends {keepSession: true}: an expired token there is said, not a sign-out). */
export async function uploadForIdentification(files, config) {
  const keys = [];
  for (const file of files) {
    const body = {
      filename: file.name,
      content_type: file.type || 'image/jpeg',
      photo_type: 'other',
      taxon_name: '',
      source_type: 'ai_identify',
    };
    const { data } = await (config ? api.post('/photos/presign', body, config) : api.post('/photos/presign', body));
    const put = await fetch(resolvePhotoUrl(data.presign_url), {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'image/jpeg' },
    });
    if (!put.ok) throw new Error(`upload failed (HTTP ${put.status})`);
    keys.push(data.s3_key);
  }
  return keys;
}
